import { TextNode, type EditorConfig, type LexicalNode, type SerializedTextNode } from 'lexical';

import type { TeoMarker } from '../EditorMarkerManager.svelte';
import type EditorContent from './EditorContent';

export interface SerializedTeoTextNode extends SerializedTextNode {
  markers?: TeoMarker[];
  key: string;
  charNum?: number;
}

export class TeoTextNode extends TextNode {
  markers: TeoMarker[] = [];
  editorContent: EditorContent;
  initialized: boolean = false;
  // registeredMarkers: { [key: string]: boolean } = {};

  constructor(editorContent: EditorContent, markers: TeoMarker[], text?: string, key?: string) {
    super(text, key);
    this.editorContent = editorContent;
    markers.forEach((marker) => {
      this.addMarker(marker);
    });
    this.initialized = true;
  }

  static override getType(): string {
    return 'teotext';
  }

  static override clone(node: TeoTextNode): TeoTextNode {
    if (!node.initialized) {
      console.error('TeoTextNode.clone: node not initialized yet, cannot clone:', node);
      throw new Error('TeoTextNode.clone: node not initialized yet, cannot clone');
    }
    const newNode = new TeoTextNode(node.editorContent, node.markers, node.__text, node.__key);
    return newNode;
  }

  override splitText(...splitOffsets: Array<number>): Array<TextNode> {
    if (!this.initialized) {
      console.error('TeoTextNode.split: node not initialized yet, cannot split:', this);
      throw new Error('TeoTextNode.split: node not initialized yet, cannot split');
    }
    // Snapshot BEFORE the split
    const preSplitMarkers = this.markers.map((m) => ({ ...m }));

    const nodes = super.splitText(...splitOffsets);
    nodes.forEach((node) => {
      if (node instanceof TeoTextNode) {
        preSplitMarkers.forEach((m) => node.addMarker({ ...m }));
      } else {
        console.error('TeoTextNode.splitText: split node is not TeoTextNode:', node);
      }
    });
    return nodes;
  }

  addMarker(marker: TeoMarker): void {
    if (!this.markers.some((existingMarker) => existingMarker.id === marker.id)) {
      marker.isHighlighted = false;
      this.markers.push(marker);
      this.registerMarker(marker);
    } else if (
      this.markers.some(
        (existingMarker) => existingMarker.id === marker.id && existingMarker.type !== marker.type,
      )
    ) {
      console.warn(
        'TeoTextNode.addMarker: marker with same id but different type already exists:',
        marker,
        this.markers,
      );
    }
  }

  clearMarkers(markerIds?: number[]): void {
    if (markerIds) {
      for (let i = this.markers.length - 1; i >= 0; i--) {
        if (this.markers[i] && markerIds.includes(this.markers[i]!.id)) {
          this.markers.splice(i, 1);
        }
      }
    } else {
      this.markers.length = 0;
    }
  }

  checkForMarkerType(type: string): TeoMarker | null {
    return this.markers.find((marker) => marker.type === type) || null;
  }

  $highlightMarker(markerType: string): void {
    // Not needed for now

    // const marker =
    this.checkForMarkerType(markerType);
    // if (marker) {
    //   marker.isHighlighted = true;
    //   let style = '';
    //   if (marker.isClickable) {
    //     style += 'cursor: pointer;';
    //   }
    //   this.setStyle(style);
    // }
  }

  $removeHighlights(): void {
    // empty highlightedMarkers
    this.markers.forEach((marker) => {
      marker.isHighlighted = false;
    });
    this.setStyle('');
  }

  // override afterCloneFrom(prevNode: this): void {
  //   console.log('TeoTextNode.afterCloneFrom:', prevNode);
  //   super.afterCloneFrom(prevNode);
  // }

  override createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.addEventListener('click', () => {
      console.log('TeoTextNode markers:', this.markers);
    });
    // Not needed for now

    // element.addEventListener('click', () => {
    //   this.markers.forEach((marker) => {
    //     if (marker.isClickable && marker.isHighlighted) {
    //       this.editorContent.onMarkerClick(marker.id);
    //     }
    //   });
    // });
    return element;
  }

  static override importJSON(sNode: SerializedTextNode): TeoTextNode {
    const node = super.importJSON(sNode) as TeoTextNode;
    node.markers = []; //(sNode.markers as TeoMarker[]) || [];
    return node;
  }

  override updateDOM(prevNode: this, element: HTMLElement, config: EditorConfig): boolean {
    const recreate = super.updateDOM(prevNode, element, config);
    // this.registerMarkers(element);
    return recreate;
  }

  registerMarker(marker: TeoMarker): void {
    this.editorContent.addTextNode(marker.id, this.getKey());
  }

  unRegisterMarker(marker: TeoMarker): void {
    this.editorContent.removeTextNode(marker.id, this.getKey());
  }

  override exportJSON(): SerializedTeoTextNode {
    return {
      ...super.exportJSON(),
      key: this.__key,
      charNum: this.__text.length,
      markers: this.markers,
    };
  }
}

export function $isTeoTextNode(node: LexicalNode | null | undefined): node is TeoTextNode {
  return node instanceof TeoTextNode;
}
